import React, { useState, useId } from 'react';

const MAX_NAME_LENGTH = 50;

// Name + API token form used on the first-run screen and in the account panel.
export const AccountForm = ({
  onSubmit,
  existingNames = [],
  submitLabel = 'Add Account',
  onCancel,
  showHelp = false,
  fullWidthSubmit = false
}) => {
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [errors, setErrors] = useState({});
  const nameId = useId();
  const tokenId = useId();

  const validate = () => {
    const nextErrors = {};
    const trimmedName = name.trim();
    const trimmedToken = token.trim();

    if (!trimmedName) {
      nextErrors.name = 'Account name is required.';
    } else if (trimmedName.length > MAX_NAME_LENGTH) {
      nextErrors.name = `Account name must be ${MAX_NAME_LENGTH} characters or fewer.`;
    } else if (existingNames.some((existing) => existing.toLowerCase() === trimmedName.toLowerCase())) {
      // Cached data is stored per account name, so names have to be unique.
      nextErrors.name = `An account named "${trimmedName}" already exists. Choose a different name.`;
    }

    if (!trimmedToken) {
      nextErrors.token = 'API token is required.';
    } else if (/\s/.test(trimmedToken)) {
      nextErrors.token = 'API token must not contain spaces. Paste the token exactly as DigitalOcean shows it.';
    }

    return nextErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit(name.trim(), token.trim());
    setName('');
    setToken('');
    setShowToken(false);
  };

  const clearError = (field) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="form-group">
        <label htmlFor={nameId}>Account Name <span className="required-mark" aria-hidden="true">*</span></label>
        <input
          type="text"
          id={nameId}
          placeholder="e.g., Production, Development, etc."
          value={name}
          onChange={(e) => { setName(e.target.value); clearError('name'); }}
          maxLength={MAX_NAME_LENGTH}
          autoComplete="off"
          aria-required="true"
          aria-invalid={errors.name ? 'true' : undefined}
          aria-describedby={errors.name ? `${nameId}-error` : (showHelp ? `${nameId}-help` : undefined)}
        />
        {errors.name ? (
          <span id={`${nameId}-error`} className="field-error" role="alert">{errors.name}</span>
        ) : showHelp && (
          <small id={`${nameId}-help`} className="form-text">
            Give this account a meaningful name to identify it later
          </small>
        )}
      </div>

      <div className="form-group">
        <label htmlFor={tokenId}>DigitalOcean API Token <span className="required-mark" aria-hidden="true">*</span></label>
        <div className="input-with-action">
          <input
            type={showToken ? 'text' : 'password'}
            id={tokenId}
            placeholder="Enter API token with billing read access"
            value={token}
            onChange={(e) => { setToken(e.target.value); clearError('token'); }}
            autoComplete="off"
            spellCheck="false"
            aria-required="true"
            aria-invalid={errors.token ? 'true' : undefined}
            aria-describedby={errors.token ? `${tokenId}-error` : (showHelp ? `${tokenId}-help` : undefined)}
          />
          <button
            type="button"
            className="btn-secondary input-action"
            onClick={() => setShowToken((prev) => !prev)}
            aria-pressed={showToken}
            aria-label={showToken ? 'Hide API token' : 'Show API token'}
          >
            {showToken ? 'Hide' : 'Show'}
          </button>
        </div>
        {errors.token ? (
          <span id={`${tokenId}-error`} className="field-error" role="alert">{errors.token}</span>
        ) : showHelp && (
          <small id={`${tokenId}-help`} className="form-text">
            Create a read-only token in your DigitalOcean account settings
          </small>
        )}
      </div>

      <div className="form-buttons">
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        )}
        <button type="submit" style={fullWidthSubmit ? { width: '100%' } : undefined}>{submitLabel}</button>
      </div>
    </form>
  );
};
